import { ColumnDef } from '@tanstack/react-table'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { useCallback, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { PiMagnifyingGlass } from 'react-icons/pi'
import { toast } from 'react-toastify'
import {
  ActionsDropdown,
  createEditAction,
  createDeleteAction,
} from '../../../components/Buttons/ActionsDropdown'
import confirm from '../../../components/dialogConfirm'
import {
  ClientDataTable,
  useIsRowHovered,
} from '../../../components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import { FilterChipsComponent } from '../../../components/Filters/components/FilterChips/FilterChips'
import { IconContainer, Input } from '../../../components/Filters/TableSearch'
import { EditSavedFilterModal } from '../../../components/Filters/saved-filters/EditSavedFilterModal'
import * as CommonS from '../../../components/Tables/CommonTable.styles'
import { DateCell } from '../../../components/Tables/CommonTableCells/DateCell'
import {
  FilterIdentifier,
  SavedView,
  SavedViewTableLabels,
} from '../../../models/saved_view'
import useApiService from '../../../services/api'
import * as S from './styles'

function HoverableFilterChips({
  payload,
  identifier,
  rowId,
}: {
  payload: SavedView['payload']
  identifier: FilterIdentifier
  rowId: string
}) {
  const isHovered = useIsRowHovered(rowId)

  return (
    <div className="py-2">
      <FilterChipsComponent
        filterState={{
          valueFilters: payload.valueFilters,
          rangeFilters: payload.rangeFilters,
          zipCodeFilter: payload.zipCodeFilter,
          criteria: payload.criteria,
          search: payload.search,
        }}
        identifier={identifier}
        isHovered={isHovered}
      />
    </div>
  )
}

export function SavedFiltersTable() {
  const api = useApiService()

  const { data: categories } = api.useGetActiveCompanyCategoriesAndTypes()
  const {
    data: rawData,
    isLoading,
    refetch,
    isFetching,
  } = api.useGetAllSavedFilters()

  const [editModalOpen, setEditModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedFilter, setSelectedFilter] = useState<SavedView | null>(null)

  // Client-side search: all data is loaded at once (no pagination), so filtering in-memory is sufficient.
  const data = useMemo(() => {
    if (!searchQuery || !rawData) return rawData
    const q = searchQuery.toLowerCase()
    return rawData.filter((item) => item.name?.toLowerCase().includes(q))
  }, [rawData, searchQuery])

  const handleDelete = useCallback(
    async (filterId: number) => {
      const confirmed = await confirm(
        'Are you sure you want to delete this saved filter?',
        'Delete Saved Filter',
        'This action cannot be undone.',
        { confirmLabel: 'Delete', buttonVariant: 'danger' }
      )

      if (!confirmed) {
        return
      }

      try {
        await api.deleteFilter(filterId)
        toast.success('Saved filter deleted successfully')
        refetch()
      } catch (err) {
        console.error('Failed to delete saved filter:', err)
        toast.error('Failed to delete saved filter')
      }
    },
    [api, refetch]
  )

  const handleEdit = useCallback((filter: SavedView) => {
    setSelectedFilter(filter)
    setEditModalOpen(true)
  }, [])

  const columns = useMemo<ColumnDef<SavedView, any>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'filters',
        header: 'Filters',
        size: 500,
        cell: (info) => (
          <HoverableFilterChips
            payload={info.row.original.payload}
            identifier={info.row.original.identifier}
            rowId={info.row.id}
          />
        ),
      },
      {
        accessorKey: 'identifier',
        header: 'Table',
        cell: (info) => {
          if (info.getValue().toString().startsWith('companies-')) {
            const id = parseInt(info.getValue().toString().split('-')[1])
            let name = ''
            if (categories) {
              loop: for (const c of categories) {
                for (const s of c.sub_categories) {
                  if (s.id === id) {
                    name = s.name
                    break loop
                  }
                }
              }
            }

            return name
          } else {
            return SavedViewTableLabels[info.getValue() as FilterIdentifier]
          }
        },
      },
      {
        accessorKey: 'shared',
        size: 100,
        header: 'Shared',
        cell: (info) => (info.getValue() ? 'Yes' : 'No'),
      },
      {
        id: 'user__first_name',
        header: 'Created By',
        cell: (info) => {
          return (
            info.row.original?.user?.first_name +
            ' ' +
            info.row.original?.user?.last_name
          )
        },
      },
      {
        accessorKey: 'created',
        header: 'Date Created',
        cell: DateCell,
      },
      {
        accessorKey: 'modified',
        header: 'Date Modified',
        cell: DateCell,
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 70,
        meta: {
          rightAlign: true,
        },
        cell: ({ row }) => (
          <ActionsDropdown
            actions={[
              createEditAction(() => handleEdit(row.original)),
              createDeleteAction(() => handleDelete(row.original.id)),
            ]}
          />
        ),
      },
    ],
    [categories, handleDelete, handleEdit]
  )

  if (isLoading || !rawData || isFetching) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
        }}
      >
        <Spinner animation="border" />
      </div>
    )
  }

  const descriptionText = `
  Saved filters from across all tables in First Bite. Private filters are only visible to you.
  `

  return (
    <>
      <DataTableContainer>
        <ClientDataTable
          tableHeader={
            <>
              <S.SettingsTableHeader>
                <S.TableDescriptionText>
                  <OverflownTextTooltip
                    tooltipText={descriptionText}
                    maxLines={4}
                    placement="bottom"
                  >
                    {descriptionText}
                  </OverflownTextTooltip>
                </S.TableDescriptionText>
              </S.SettingsTableHeader>
              <CommonS.SearchContainer>
                <CommonS.SearchContainerFilters>
                  <CommonS.TableSearchContainer>
                    <div style={{ position: 'relative' }}>
                      <IconContainer>
                        <PiMagnifyingGlass />
                      </IconContainer>
                      <Input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search saved filters..."
                        className="form-control"
                        style={{ width: '100%' }}
                      />
                    </div>
                  </CommonS.TableSearchContainer>
                </CommonS.SearchContainerFilters>
              </CommonS.SearchContainer>
            </>
          }
          height={'100%'}
          stickyFirstColumn={true}
          stickyLastColumn={true}
          enableRowHover={true}
          enableRightCellAlignment={true}
          data={data || []}
          columns={columns}
        />
      </DataTableContainer>
      <EditSavedFilterModal
        show={editModalOpen}
        handleClose={() => {
          setEditModalOpen(false)
          setSelectedFilter(null)
        }}
        savedFilter={selectedFilter}
        onFilterUpdated={refetch}
      />
    </>
  )
}
